import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Button, Container, TextField, Typography, Paper
} from '@mui/material';
import BreadCrumbs from '../components/Breadcrumbs';
import { getPluginEdit, updatePluginFile } from '../http/PluginAPI';
import Editor from '@monaco-editor/react';
import { useSnackbar } from '../components/snackbar/SnackbarProvider';
import { getApiErrorMessage } from '../http/apiError';

const PluginEdit = () => {
    const { plugin_id, addition_id, filename } = useParams();
    const navigate = useNavigate();
    const snackbar = useSnackbar();

    const [code, setCode] = useState('');
    const [fileName, setFileName] = useState(decodeURIComponent(filename));
    const [readOnly, setReadOnly] = useState(false);

    const hrefs = [
        { href: `/plugin/${plugin_id}`, name: 'Редактирование плагина' }
    ];

    const getExtension = (name) => name.split('.').pop().toLowerCase();

    const isEditableExtension = (ext) => ['cs', 'json'].includes(ext);

    const getLanguage = () => {
        const ext = getExtension(fileName);
        if (ext === 'cs') return 'csharp';
        if (ext === 'json') return 'json';
        return 'plaintext';
    };

    useEffect(() => {
        const ext = getExtension(fileName);
        const editable = isEditableExtension(ext);
        setReadOnly(!editable);

        getPluginEdit(parseInt(plugin_id), parseInt(addition_id), filename)
            .then(data => {
                let content = data.code || '';

                // Автоматическое форматирование JSON
                if (ext === 'json') {
                    try {
                        const json = JSON.parse(content);
                        content = JSON.stringify(json, null, 2);
                    } catch (e) {
                        console.warn('Невалидный JSON, пропущено форматирование');
                    }
                }

                setCode(content);
            })
            .catch(console.error);
    }, [plugin_id, addition_id, filename]);

    const handleSave = async () => {
        try {
            await updatePluginFile({
                plugin_id: parseInt(plugin_id),
                addition_id: parseInt(addition_id),
                filename: fileName,
                code
            });
            snackbar.success('Файл успешно сохранён');
            navigate(`/plugin/${plugin_id}`);
        } catch (e) {
            console.error(e);
            snackbar.error(getApiErrorMessage(e, 'Ошибка при сохранении')); 
        }
    };

    return (
        <Container maxWidth="xl">
            <Box sx={{ width: '100%', marginTop: 3, marginBottom: 3 }}>
                <BreadCrumbs hrefs={hrefs} name={`Файл: ${fileName}`} />
            </Box>

            <Typography variant="h5" gutterBottom>
                Редактирование файла
            </Typography>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Имя файла</Typography>
                <TextField
                    fullWidth
                    value={fileName}
                    onChange={(e) => setFileName(e.target.value)}
                />
            </Paper>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Содержимое файла</Typography>
                <Editor
                    height="600px"
                    language={getLanguage()}
                    theme="vs-dark"
                    value={code}
                    onChange={(value) => setCode(value || '')}
                    options={{
                        fontSize: 14,
                        minimap: { enabled: false },
                        readOnly,
                        scrollBeyondLastLine: false,
                    }}
                />
                {!isEditableExtension(getExtension(fileName)) && (
                    <Typography sx={{ mt: 1 }} variant="body2" color="error">
                        Файл не поддерживается для редактирования. Доступны только .cs и .json
                    </Typography>
                )}
            </Paper>

            <Box textAlign="right">
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={readOnly}
                >
                    Сохранить
                </Button>
            </Box>
        </Container>
    );
};

export default PluginEdit;
